import React from "react";
import {
  Box,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  Image,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";

function AdaptiveLogoGrid({ logos }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.02)");

  const n = logos?.length ?? 0;

  // Layout rules:
  // 1 logo -> one big centered card
  // 2 logos -> 2 columns (bigger cards)
  // >=3 -> auto-fit grid
  const templateColumns =
    n <= 1
      ? "minmax(280px, 520px)"
      : n === 2
      ? { base: "1fr", md: "repeat(2, minmax(260px, 1fr))" }
      : "repeat(auto-fit, minmax(180px, 1fr))";

  // Make cards/logos larger when there are few items
  const cardMinH = n <= 2 ? { base: "120px", md: "150px" } : { base: "96px", md: "110px" };
  const logoMaxH = n <= 2 ? { base: "90px", md: "110px" } : { base: "60px", md: "72px" };
  const px = n <= 2 ? { base: 6, md: 8 } : { base: 5, md: 6 };
  const py = n <= 2 ? { base: 5, md: 6 } : { base: 4, md: 5 };

  return (
    <SimpleGrid
      templateColumns={templateColumns}
      spacing={5}
      justifyContent="center"
      justifyItems="center"
      // for 1 logo: keep it from stretching too wide on large monitors
      maxW={n <= 2 ? "900px" : "none"}
      mx="auto"
    >
      {logos.map((src) => (
        <Box
          key={src}
          w="100%"
          border="0px solid"
          borderColor={border}
          borderRadius="18px"
          bg={cardBg}
          minH={cardMinH}
          px={px}
          py={py}
          display="flex"
          alignItems="center"
          justifyContent="center"
          transition="all 180ms ease"
          _hover={{ transform: "translateY(-2px)" }}
        >
          <Image src={src} alt={src} maxH={logoMaxH} maxW="100%" objectFit="contain" />
        </Box>
      ))}
    </SimpleGrid>
  );
}

function RegionBlock({ title, mapSrc, logos, columns = { base: 2, md: 4 } }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const bg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");

  // Map height: responsive and bounded, so it won’t dominate.
  // - On mobile: ~180-240px
  // - On desktop: ~260-340px (depending on viewport)
  const maxH="clamp(200px, 22vw, 340px)"

  return (
    <Box
      border="1px solid"
      borderColor={border}
      borderRadius="28px"
      bg={bg}
      overflow="hidden"
      boxShadow={useColorModeValue("0 14px 40px rgba(0,0,0,0.08)", "none")}
    >
      {/* Header */}
      <Box p={{ base: 5, md: 6 }}>
        <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
          {title}
        </Heading>
        <Text color={subColor} mt={1}>
          Collaborating organizations and partners in this region.
        </Text>
      </Box>

      <Divider borderColor={border} />

      {/* Map (responsive size, no crop) */}
      <Box px={{ base: 5, md: 6 }} pt={{ base: 5, md: 6 }} w="80%" mx="auto">
        <Box
          border="1px solid"
          borderColor={border}
          borderRadius="22px"
          bg={useColorModeValue("rgba(49,83,105,0.04)", "rgba(255,255,255,0.04)")}
          overflow="hidden"
        >
          <Box
            w="100%"
            // shorter than before so it doesn’t feel huge
            aspectRatio={{ base: 16 / 9, md: 21 / 9 }}
            // hard cap the visual height responsively
            maxH={maxH}
            mx="auto"
            display="flex"
            alignItems="center"
            justifyContent="center"
            // less padding so the map uses the frame efficiently
            p={{ base: 3, md: 4 }}
          >
            <Image
              src={mapSrc}
              alt={`${title} map`}
              w="100%"
              h="100%"
              objectFit="contain"   // ✅ never crop
            />
          </Box>
        </Box>
      </Box>

      {/* Logos */}
      <Box p={{ base: 5, md: 6 }}>
        <AdaptiveLogoGrid logos={logos} />
      </Box>
    </Box>
  );
}



function PageHeader({ title, subtitle }) {
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, rgba(49,83,105,0.10), rgba(220,36,44,0.10))",
    "linear-gradient(135deg, rgba(49,83,105,0.16), rgba(220,36,44,0.14))"
  );
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  return (
    <Box
      pt={{ base: "140px", md: "140px" }}
      pb={{ base: 10, md: 12 }}
      bg={heroBg}
      borderBottom="1px solid"
      borderColor={border}
    >
      <Container maxW="1200px">
        <Stack spacing={3}>
          <HStack spacing={2} flexWrap="wrap">
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
              border="1px solid"
              borderColor={border}
              fontWeight="800"
            >
              University of Ottawa
            </Tag>
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("rgba(220,36,44,0.10)", "rgba(220,36,44,0.20)")}
              color={accent}
              fontWeight="900"
            >
              SITE-CAV
            </Tag>
          </HStack>

          <Heading color={titleColor} fontSize={{ base: "3xl", md: "4xl" }} letterSpacing="tight">
            {title}
          </Heading>

          {subtitle ? (
            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="80ch">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
      </Container>
    </Box>
  );
}

function Section({ title, subtitle, children }) {
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");

  return (
    <Box py={{ base: 10, md: 12 }}>
      <Container maxW="1200px">
        <Stack spacing={2} mb={{ base: 5, md: 7 }}>
          <Heading fontSize={{ base: "2xl", md: "3xl" }} color={titleColor} letterSpacing="tight">
            {title}
          </Heading>
          {subtitle ? (
            <Text color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="900px">
              {subtitle}
            </Text>
          ) : null}
        </Stack>
        <Divider borderColor={border} mb={{ base: 6, md: 8 }} />
        {children}
      </Container>
    </Box>
  );
}

function PersonCard({ name, role, org, photo, note }) {
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)");
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const titleColor = useColorModeValue("#315369", "white");
  const subColor = useColorModeValue("gray.700", "gray.300");
  const accent = "#DC242C";

  return (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="24px"
      p={{ base: 5, md: 6 }}
      boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.08)", "none")}
      transition="all 180ms ease"
      _hover={{ transform: "translateY(-2px)" }}
      h="100%"
    >
      <Stack spacing={4} align="center" textAlign="center" h="100%">
        {/* Circular frame, no crop */}
        <Box
          w={{ base: "120px", md: "170px" }}
          h={{ base: "120px", md: "170px" }}
          borderRadius="999px"
          border="1px solid"
          borderColor={border}
          overflow="hidden"
          bg={useColorModeValue("rgba(49,83,105,0.04)", "rgba(255,255,255,0.03)")}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={2}
        >
          <Image
            src={photo}
            alt={name}
            w="100%"
            h="100%"
            objectFit="contain"  // ✅ preserves aspect ratio, no face cropping
          />
        </Box>

        <Box>
          <Text fontWeight="900" color={titleColor} fontSize={{ base: "md", md: "lg" }}>
            {name}
          </Text>

          {role ? (
            <Text fontWeight="800" color={accent} fontSize="sm" mt={1}>
              {role}
            </Text>
          ) : null}

          {note ? (
            <Text fontWeight="700" color={subColor} fontSize="sm" mt={1}>
              {note}
            </Text>
          ) : null}

          {org ? (
            <Text color={subColor} fontSize="sm" mt={1}>
              {org}
            </Text>
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}

function LogoCard({ src }) {
  const border = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)");
  const bg = useColorModeValue("white", "rgba(255,255,255,0.03)");

  return (
    <Box
      bg={bg}
      border="1px solid"
      borderColor={border}
      borderRadius="20px"
      p={5}
      minH="110px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Image src={src} alt={src} maxH="70px" objectFit="contain" />
    </Box>
  );
}

export default function NextconMembers() {
  const isMobile = useSelector((state) => state.windowSize.isMobile);

  // --- DATA (easy to edit later) ---
  const leadership = [
    {
      name: "Burak Kantarci",
      role: "Principal Investigator and Research Director",
      org: "University of Ottawa",
      photo: "BurakNewProfile.jpeg",
    },
    {
      name: "Hussein Mouftah",
      role: "Co-PI and research co-director",
      note: "(Jan 2025 – Oct 2025)",
      org: "University of Ottawa",
      photo: "husseinmouftah.jpg",
    },
  ];

  const researchTeam = [
    { name: "Melike Erol-Kantarci", org: "University of Ottawa", photo: "MelikeKantarci.jpg" },
    { name: "Claude D'Amours", org: "University of Ottawa", photo: "Claude D'Amours.png" },
    { name: "Abdallah Shami", org: "Western University", photo: "Abdallah Shami.jpg" },
    { name: "Birsen Donmez", org: "University of Toronto", photo: "BirsenDonmez.jpg" },
    { name: "Xianbin Wang", org: "Western University", photo: "XianbinWang.jpg" },
    { name: "Lionel Briand", org: "University of Ottawa", photo: "LionelBriand.jpg" },
    { name: "Guy-Vincent Jourdan", org: "University of Ottawa", photo: "Guy-Vincent Jourdan.jpg" },
    { name: "Elizabeth Judge", org: "University of Ottawa", photo: "ElizabethJudge.jpg" },
    /*{ name: "Abdulmotaleb El Saddik", org: "University of Ottawa", photo: "el_saddik_profile.jpg" },
    { name: "Jonathan Calof", org: "University of Ottawa", photo: "Calof picture 2022.jpg" },*/
  ];

  const management = [
    { name: "Peter MacKinnon", role: "Project Executive Manager", photo: "peter mackinnon.jpg" },
    { name: "Didem Cicek Simsek", role: "Project Manager – Integration Architect", photo: "NEXTCON-Didem.jpg" },
    { name: "Iraz Eliz Deniz", role: "Project Coordinator", photo: "Elis.jpg" },
    { name: "Jaipreet Bindra", role: "Commercialization Lead", photo: "JBCover.jpg" },
  ];

  const committee = [
    { name: "Sonya Shorey", role: "CEO", org: "Invest Ottawa", photo: "sonya.jpg" },
    { name: "Arda Ozgun", role: "CEO", org: "Edge Signal", photo: "arda-ozgun.jpg" },
    { name: "Dams Narayanan", role: "Director, CIO – Innovation & Strategy", org: "Canada Post", photo: "DamsNarayanan.png" },
  ];

  const digital = [
    { name: "Murat Arda Onsu", role: "Digital Communication Coordinator", org: "University of Ottawa", photo: "MuratArda.png" },
  ];

  const ontarioLogos = [
    "I-Ontario Area-X.0-logo.png",
    "I-Ontario Aurrigo-Logo.png",
    "I-Ontario ThinkRF.png",
    "I-Ontario Edge Signal.png",
    "I-Ontario Lytica.jpg",
    "I-Ontario Quanser.jpg",
    "I-Ontario s2e.jpg",
    "I-Ontario canada post.png",
  ];

  return (
    <Box>
      <PageHeader
        title="SITE-CAV Team"
        subtitle="Principal Investigators, Co-Investigators, project management, and collaborators supporting secure and trustworthy connected mobility."
      />

      <Section title="Principal Investigators">
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
          {leadership.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </SimpleGrid>
      </Section>

      <Section title="Co-Investigators">
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 5, md: 7 }}>
          {researchTeam.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </SimpleGrid>
      </Section>

      <Section title="Project Management and Administration">
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 5, md: 7 }}>
          {management.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </SimpleGrid>
      </Section>

      <Section title="Strategic Assessment Committee">
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
          {committee.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </SimpleGrid>
      </Section>

      <Section title="Digital Communication Coordinator">
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={6}>
          {digital.map((p) => (
            <PersonCard key={p.name} {...p} />
          ))}
        </SimpleGrid>
      </Section>

      {/* Big image, no crop */}
      <Box pb={{ base: 10, md: 12 }}>
        <Container maxW="1200px">
          <Box
            borderRadius="28px"
            overflow="hidden"
            border="1px solid"
            borderColor={useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.10)")}
            bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
            boxShadow={useColorModeValue("0 16px 50px rgba(0,0,0,0.10)", "none")}
          >
            <Box
              w="100%"
              aspectRatio={{ base: 4 / 3, md: 16 / 9 }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={{ base: 4, md: 6 }}
            >
              <Image src="about us general.png" alt="SITE-CAV overview" w="100%" h="100%" objectFit="contain" />
            </Box>
          </Box>
        </Container>
      </Box>

      <Section
        title="Collaborators and Industry Board"
        subtitle="Regional partners supporting technology transfer, testbeds, and real-world impact."
        >
        <Stack spacing={{ base: 8, md: 10 }}>
            <RegionBlock
            title="Canada – Ontario"
            mapSrc="I-Ontario.png"
            logos={[
                "I-Ontario Area-X.0-logo.png",
                "I-Ontario Aurrigo-Logo.png",
                "I-Ontario ThinkRF.png",
                "I-Ontario Edge Signal.png",
                "I-Ontario Lytica.jpg",
                "I-Ontario Quanser.jpg",
                "I-Ontario s2e.jpg",
                "I-Ontario canada post.png",
            ]}
            columns={{ base: 2, md: 4 }}
            />

            <RegionBlock
            title="Canada – Alberta"
            mapSrc="I-Alberta.png"
            logos={["I-Alberta Wedge.png"]}
            columns={{ base: 1, md: 2 }}
            />

            <RegionBlock
            title="USA – California"
            mapSrc="I-California.png"
            logos={["I-California Trusted Mobi.png"]}
            columns={{ base: 1, md: 2 }}
            />
        </Stack>
        </Section>
    </Box>
  );
}
